import { Button } from 'antd';
import { observer } from 'mobx-react-lite';
import { useEffect } from 'react';
import { Empty } from '@/components/common/Empty';
import { useLoginDialog } from '@/contexts/LoginDialogContext';
import { authStore } from '@/stores';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

export const ProtectedRoute = observer(({ children }: ProtectedRouteProps) => {
  const { openDialog } = useLoginDialog();

  // biome-ignore lint/correctness/useExhaustiveDependencies: authStore fields are MobX observables that must stay in the effect dependencies.
  useEffect(() => {
    if (!authStore.initializing && !authStore.isAuthenticated) {
      openDialog();
    }
  }, [authStore.initializing, authStore.isAuthenticated, openDialog]);

  if (
    authStore.initializing ||
    (authStore.isAuthenticated && !authStore.adminAccessInitialized)
  ) {
    return (
      <div className="min-h-screen bg-gray-50/50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent"></div>
          <p className="mt-4 text-sm text-muted-foreground">
            {authStore.initializing ? '加载中...' : '校验访问权限中...'}
          </p>
        </div>
      </div>
    );
  }

  if (!authStore.isAuthenticated) {
    return <div className="min-h-screen bg-gray-50/50" />;
  }

  if (!authStore.isAdmin) {
    const description =
      authStore.adminAccessError ||
      '当前账号不在管理后台白名单中，请联系管理员开通权限。';

    return (
      <div className="min-h-screen bg-gray-50/50 flex items-center justify-center px-6">
        <div className="w-full max-w-md rounded-2xl border border-border bg-background p-8 shadow-sm">
          <Empty description={description} className="py-8" />
          <div className="flex justify-center gap-3">
            {authStore.adminAccessError ? (
              <Button
                onClick={() => void authStore.checkAdminAccess(true)}
                loading={authStore.adminAccessChecking}
              >
                重试
              </Button>
            ) : null}
            <Button
              type="primary"
              onClick={() => void authStore.logout()}
              loading={authStore.isLoggingIn}
            >
              切换账号
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return <>{children}</>;
});
